import { useLayoutEffect, useMemo } from 'react';
import type { PropsWithChildren } from 'react';

import { useAuthService } from '~domains/auth';

import { setRegisteredQueryClient } from '~shared/lib/query-client-registry';

import { isAuthError } from '@breezeventures/web-lib-ph-request/errors';
import { useNotifications } from '@pfa/front-notifications';
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
  QueryMeta,
} from '@tanstack/react-query';

const shouldSkipGlobalErrorHandling = (meta: QueryMeta | undefined) => {
  return meta && 'skipGlobalErrorHandling' in meta;
};

export const PortalQueryClientProvider = ({ children }: PropsWithChildren) => {
  const { logout } = useAuthService();
  const notificatons = useNotifications();

  const queryClient = useMemo(() => {
    const handleError: QueryCache['config']['onError'] = (error, query) => {
      if (isAuthError(error as Error)) {
        logout();
        return;
      }
      if (!shouldSkipGlobalErrorHandling(query.options.meta)) {
        notificatons.error('Something went wrong. Please, try again later');
      }
    };

    return new QueryClient({
      defaultOptions: {
        queries: {
          retry: (retryCount: number, error: Error) => {
            if (isAuthError(error)) {
              return false;
            }

            return retryCount <= 3;
          },
        },
        mutations: {
          retry: false,
        },
      },
      queryCache: new QueryCache({ onError: handleError }),
    });
  }, []);

  useLayoutEffect(() => {
    setRegisteredQueryClient(queryClient);
    return () => setRegisteredQueryClient(undefined);
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};
